import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Building2,
  Layers,
  DoorOpen,
  TreePine,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Settings,
  Building,
} from 'lucide-react';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import {
  // New API-based functions with automatic token refresh
  getCampusesWithInfrastructure,
  getCampusInfrastructure,
  configureCampusInfrastructureNew,
  configureBuildingFloorsNew,
  createBuildingNew,
  updateBuildingNew,
  deleteBuildingNew,
  createFloorNew,
  updateFloorNew,
  deleteFloorNew,
  createRoomNew,
  updateRoomNew,
  deleteRoomNew,
  createTerrainNew,
  updateTerrainNew,
  deleteTerrainNew,
  RoomCategory,
  ROOM_CATEGORY_LABELS,
  ROOM_SUB_CATEGORIES,
  TERRAIN_TYPES,
  getSubCategoryLabel,
  getTerrainTypeLabel,
  type CampusWithInfrastructure,
  type Building as BuildingType,
  type Floor,
  type Room,
  type Terrain,
  type ConfigureCampusInfrastructureDto,
} from '@/services/campusInfrastructureService';

// Modal de confirmation
interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string | React.ReactNode;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'danger' | 'warning' | 'info';
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  variant = 'warning',
}) => {
  if (!isOpen) return null;

  const variantStyles = {
    danger: {
      icon: <AlertTriangle className="w-6 h-6 text-red-600" />,
      iconBg: 'bg-red-100',
      confirmBtn: 'bg-red-600 hover:bg-red-700',
    },
    warning: {
      icon: <AlertCircle className="w-6 h-6 text-amber-600" />,
      iconBg: 'bg-amber-100',
      confirmBtn: 'bg-amber-600 hover:bg-amber-700',
    },
    info: {
      icon: <AlertCircle className="w-6 h-6 text-blue-600" />,
      iconBg: 'bg-blue-100',
      confirmBtn: 'bg-blue-600 hover:bg-blue-700',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onCancel} />
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 transform transition-all">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-full ${styles.iconBg}`}>{styles.icon}</div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
              <div className="text-sm text-gray-600">{message}</div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 text-white rounded-lg transition-colors ${styles.confirmBtn}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Configuration wizard step types
type WizardStep = 'buildings' | 'terrains' | 'floors' | 'rooms' | 'done';

interface WizardState {
  step: WizardStep;
  campusId: string;
  hasMultipleBuildings: boolean;
  buildings: { name: string }[];
  hasTerrains: boolean;
  terrains: { name: string; type: string }[];
  currentBuildingIndex: number;
  currentFloorIndex: number;
  floorsConfig: { hasBasement: boolean; hasGroundFloor: boolean; numberOfFloors: number };
  floors: Floor[];
}

export const GestionCampusPage: React.FC = () => {
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.INFRASTRUCTURE_MANAGE);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Campus data
  const [campuses, setCampuses] = useState<CampusWithInfrastructure[]>([]);
  const [selectedCampus, setSelectedCampus] = useState<CampusWithInfrastructure | null>(null);
  const [expandedBuildings, setExpandedBuildings] = useState<Set<string>>(new Set());
  const [expandedFloors, setExpandedFloors] = useState<Set<string>>(new Set());
  // Scroll position to restore after an in-place reload (see useLayoutEffect).
  const pendingScrollRef = useRef<number | null>(null);

  // Configuration wizard
  const [showWizard, setShowWizard] = useState(false);
  const [wizardState, setWizardState] = useState<WizardState | null>(null);

  // Edit modals
  const [editingBuilding, setEditingBuilding] = useState<BuildingType | null>(null);
  const [editingFloor, setEditingFloor] = useState<{ floor: Floor; buildingId: string } | null>(null);
  const [editingRoom, setEditingRoom] = useState<{ room: Room; floorId: string; buildingId: string } | null>(null);
  const [editingTerrain, setEditingTerrain] = useState<Terrain | null>(null);

  // Add modals
  const [addingBuilding, setAddingBuilding] = useState(false);
  const [addingFloorTo, setAddingFloorTo] = useState<string | null>(null);
  const [addingRoomTo, setAddingRoomTo] = useState<{ floorId: string; buildingId: string } | null>(null);
  const [addingTerrain, setAddingTerrain] = useState(false);

  // Delete modals
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    type: 'building' | 'floor' | 'room' | 'terrain';
    item: any;
    parentIds?: { buildingId?: string; floorId?: string };
  }>({ isOpen: false, type: 'building', item: null });

  // Form states
  const [buildingForm, setBuildingForm] = useState({ name: '' });
  const [floorForm, setFloorForm] = useState({ floorNumber: 0, name: '' });
  const [roomForm, setRoomForm] = useState({
    name: '',
    category: RoomCategory.LOCAL_ENSEIGNEMENT,
    subCategory: 'SALLE_COURS',
    capacity: '',
  });
  const [terrainForm, setTerrainForm] = useState({ name: '', type: 'TERRAIN_FOOTBALL' });

  // Load campuses
  const loadCampuses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCampusesWithInfrastructure();
      setCampuses(data);
      if (data.length > 0 && !selectedCampus) {
        // Check if we have a pre-selected campus from navigation state
        const stateSelectedId = (location.state as { selectedCampusId?: string })?.selectedCampusId;
        if (stateSelectedId) {
          const preSelected = data.find((c) => c.id === stateSelectedId);
          setSelectedCampus(preSelected || data[0]);
        } else {
          setSelectedCampus(data[0]);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des campus');
    } finally {
      setLoading(false);
    }
  }, [selectedCampus, location.state]);

  // Reload selected campus infrastructure
  const reloadSelectedCampus = useCallback(async () => {
    if (!selectedCampus) return;

    // Remember where the user was BEFORE the reload. Restoring it is handled by
    // a useLayoutEffect below (which runs after React commits the new DOM but
    // before the browser paints) — deterministic, unlike a racing setTimeout.
    pendingScrollRef.current = window.scrollY;

    try {
      const data = await getCampusInfrastructure(selectedCampus.id);
      setSelectedCampus(data);
      // Update in campuses list
      setCampuses((prev) =>
        prev.map((c) => (c.id === data.id ? { ...data, stats: c.stats } : c))
      );
    } catch (err: any) {
      pendingScrollRef.current = null;
      setError(err.message || 'Erreur lors du chargement');
    }
  }, [selectedCampus]);

  // Restore scroll after an in-place reload so creating a floor/room/building
  // never jumps the page to the top. Runs synchronously after the DOM commit;
  // a rAF backup re-applies once the (now taller) layout has settled.
  useLayoutEffect(() => {
    const y = pendingScrollRef.current;
    if (y == null) return;
    pendingScrollRef.current = null;
    window.scrollTo({ top: y });
    requestAnimationFrame(() => window.scrollTo({ top: y }));
  }, [selectedCampus]);

  useEffect(() => {
    loadCampuses();
  }, [loadCampuses]);

  // Auto-hide success message
  useEffect(() => {
    if (!success) return;

    const timer = setTimeout(() => setSuccess(null), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  // ==================== WIZARD FUNCTIONS ====================

  const startWizard = (campus: CampusWithInfrastructure) => {
    setWizardState({
      step: 'buildings',
      campusId: campus.id,
      hasMultipleBuildings: false,
      buildings: [],
      hasTerrains: false,
      terrains: [],
      currentBuildingIndex: 0,
      currentFloorIndex: 0,
      floorsConfig: { hasBasement: false, hasGroundFloor: true, numberOfFloors: 0 },
      floors: [],
    });
    setShowWizard(true);
  };

  const handleWizardNext = async () => {
    if (!wizardState) return;

    setSaving(true);
    try {
      if (wizardState.step === 'buildings') {
        // Move to terrains step
        setWizardState({ ...wizardState, step: 'terrains' });
      } else if (wizardState.step === 'terrains') {
        // Save campus configuration and move to floors
        const configData: ConfigureCampusInfrastructureDto = {
          campusId: wizardState.campusId,
          hasMultipleBuildings: wizardState.hasMultipleBuildings,
          buildings: wizardState.hasMultipleBuildings ? wizardState.buildings : undefined,
          hasTerrains: wizardState.hasTerrains,
          terrains: wizardState.hasTerrains ? wizardState.terrains : undefined,
        };
        const result = await configureCampusInfrastructureNew(configData);
        setSelectedCampus(result);

        // If there are buildings, move to floors configuration
        if (result.buildings.length > 0) {
          setWizardState({
            ...wizardState,
            step: 'floors',
            currentBuildingIndex: 0,
          });
        } else {
          setShowWizard(false);
          setWizardState(null);
          setSuccess('Infrastructure du campus configurée avec succès');
          await reloadSelectedCampus();
        }
      } else if (wizardState.step === 'floors') {
        // Configure floors for current building
        const building = selectedCampus?.buildings[wizardState.currentBuildingIndex];
        if (building) {
          await configureBuildingFloorsNew(
            wizardState.campusId,
            building.id,
            wizardState.floorsConfig
          );

          // Move to next building or rooms step
          if (wizardState.currentBuildingIndex < (selectedCampus?.buildings.length || 1) - 1) {
            setWizardState({
              ...wizardState,
              currentBuildingIndex: wizardState.currentBuildingIndex + 1,
              floorsConfig: { hasBasement: false, hasGroundFloor: true, numberOfFloors: 0 },
            });
          } else {
            // All buildings done, move to rooms or finish
            setShowWizard(false);
            setWizardState(null);
            setSuccess('Étages configurés avec succès. Vous pouvez maintenant ajouter les salles.');
            await reloadSelectedCampus();
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la configuration');
    } finally {
      setSaving(false);
    }
  };

  // ==================== CRUD FUNCTIONS ====================

  const handleCreateBuilding = async () => {
    if (!selectedCampus) return;
    setSaving(true);
    try {
      await createBuildingNew(selectedCampus.id, { name: buildingForm.name || undefined });
      setSuccess('Bâtiment créé avec succès');
      setAddingBuilding(false);
      setBuildingForm({ name: '' });
      await reloadSelectedCampus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateBuilding = async () => {
    if (!selectedCampus || !editingBuilding) return;
    setSaving(true);
    try {
      await updateBuildingNew(selectedCampus.id, editingBuilding.id, {
        name: buildingForm.name || undefined,
      });
      setSuccess('Bâtiment mis à jour avec succès');
      setEditingBuilding(null);
      setBuildingForm({ name: '' });
      await reloadSelectedCampus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBuilding = async () => {
    if (!selectedCampus || deleteModal.type !== 'building') return;
    setSaving(true);
    try {
      await deleteBuildingNew(selectedCampus.id, deleteModal.item.id);
      setSuccess('Bâtiment supprimé avec succès');
      setDeleteModal({ isOpen: false, type: 'building', item: null });
      await reloadSelectedCampus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateFloor = async () => {
    if (!selectedCampus || !addingFloorTo) return;
    setSaving(true);
    try {
      await createFloorNew(selectedCampus.id, addingFloorTo, {
        floorNumber: floorForm.floorNumber,
        name: floorForm.name || getFloorName(floorForm.floorNumber),
      });
      setSuccess('Étage créé avec succès');
      setAddingFloorTo(null);
      setFloorForm({ floorNumber: 0, name: '' });
      await reloadSelectedCampus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateFloor = async () => {
    if (!selectedCampus || !editingFloor) return;
    setSaving(true);
    try {
      await updateFloorNew(
        selectedCampus.id,
        editingFloor.buildingId,
        editingFloor.floor.id,
        { name: floorForm.name }
      );
      setSuccess('Étage mis à jour avec succès');
      setEditingFloor(null);
      setFloorForm({ floorNumber: 0, name: '' });
      await reloadSelectedCampus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFloor = async () => {
    if (!selectedCampus || deleteModal.type !== 'floor') return;
    setSaving(true);
    try {
      await deleteFloorNew(
        selectedCampus.id,
        deleteModal.parentIds?.buildingId!,
        deleteModal.item.id
      );
      setSuccess('Étage supprimé avec succès');
      setDeleteModal({ isOpen: false, type: 'floor', item: null });
      await reloadSelectedCampus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRoom = async () => {
    if (!selectedCampus || !addingRoomTo) return;
    setSaving(true);
    try {
      await createRoomNew(selectedCampus.id, addingRoomTo.buildingId, addingRoomTo.floorId, {
        name: roomForm.name,
        category: roomForm.category,
        subCategory: roomForm.subCategory,
        capacity: roomForm.capacity ? parseInt(roomForm.capacity) : undefined,
      });
      setSuccess('Salle créée avec succès');
      setAddingRoomTo(null);
      setRoomForm({
        name: '',
        category: RoomCategory.LOCAL_ENSEIGNEMENT,
        subCategory: 'SALLE_COURS',
        capacity: '',
      });
      await reloadSelectedCampus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRoom = async () => {
    if (!selectedCampus || !editingRoom) return;
    setSaving(true);
    try {
      await updateRoomNew(
        selectedCampus.id,
        editingRoom.buildingId,
        editingRoom.floorId,
        editingRoom.room.id,
        {
          name: roomForm.name,
          category: roomForm.category,
          subCategory: roomForm.subCategory,
          capacity: roomForm.capacity ? parseInt(roomForm.capacity) : undefined,
        }
      );
      setSuccess('Salle mise à jour avec succès');
      setEditingRoom(null);
      setRoomForm({
        name: '',
        category: RoomCategory.LOCAL_ENSEIGNEMENT,
        subCategory: 'SALLE_COURS',
        capacity: '',
      });
      await reloadSelectedCampus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRoom = async () => {
    if (!selectedCampus || deleteModal.type !== 'room') return;
    setSaving(true);
    try {
      await deleteRoomNew(
        selectedCampus.id,
        deleteModal.parentIds?.buildingId!,
        deleteModal.parentIds?.floorId!,
        deleteModal.item.id
      );
      setSuccess('Salle supprimée avec succès');
      setDeleteModal({ isOpen: false, type: 'room', item: null });
      await reloadSelectedCampus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTerrain = async () => {
    if (!selectedCampus) return;
    setSaving(true);
    try {
      await createTerrainNew(selectedCampus.id, {
        name: terrainForm.name,
        type: terrainForm.type,
      });
      setSuccess('Terrain créé avec succès');
      setAddingTerrain(false);
      setTerrainForm({ name: '', type: 'TERRAIN_FOOTBALL' });
      await reloadSelectedCampus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTerrain = async () => {
    if (!selectedCampus || !editingTerrain) return;
    setSaving(true);
    try {
      await updateTerrainNew(selectedCampus.id, editingTerrain.id, {
        name: terrainForm.name,
        type: terrainForm.type,
      });
      setSuccess('Terrain mis à jour avec succès');
      setEditingTerrain(null);
      setTerrainForm({ name: '', type: 'TERRAIN_FOOTBALL' });
      await reloadSelectedCampus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTerrain = async () => {
    if (!selectedCampus || deleteModal.type !== 'terrain') return;
    setSaving(true);
    try {
      await deleteTerrainNew(selectedCampus.id, deleteModal.item.id);
      setSuccess('Terrain supprimé avec succès');
      setDeleteModal({ isOpen: false, type: 'terrain', item: null });
      await reloadSelectedCampus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ==================== HELPER FUNCTIONS ====================

  const getFloorName = (floorNumber: number): string => {
    if (floorNumber === -1) return 'Sous-sol';
    if (floorNumber === 0) return 'Rez-de-chaussée';
    if (floorNumber === 1) return '1er étage';
    return `${floorNumber}ème étage`;
  };

  const toggleBuilding = (buildingId: string) => {
    setExpandedBuildings((prev) => {
      const next = new Set(prev);
      if (next.has(buildingId)) {
        next.delete(buildingId);
      } else {
        next.add(buildingId);
      }
      return next;
    });
  };

  const toggleFloor = (floorId: string) => {
    setExpandedFloors((prev) => {
      const next = new Set(prev);
      if (next.has(floorId)) {
        next.delete(floorId);
      } else {
        next.add(floorId);
      }
      return next;
    });
  };

  // ==================== RENDER ====================

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Bâtiments & Salles</h1>
        <p className="text-gray-500 mt-1">
          Configurez l'infrastructure de vos campus : bâtiments, étages, salles et terrains
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-700">{success}</span>
        </div>
      )}

      {/* Campus Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Sélectionner un campus</label>
        <div className="flex flex-wrap gap-2">
          {campuses.map((campus) => (
            <button
              key={campus.id}
              onClick={() => setSelectedCampus(campus)}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                selectedCampus?.id === campus.id
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              }`}
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                <span>{campus.name}</span>
                {campus.isMainCampus && (
                  <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                    Principal
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {selectedCampus && (
        <>
          {/* Campus Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{selectedCampus.buildings?.length || 0}</p>
                  <p className="text-sm text-gray-500">Bâtiments</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Layers className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {selectedCampus.buildings?.reduce((sum, b) => sum + (b.floors?.length || 0), 0) || 0}
                  </p>
                  <p className="text-sm text-gray-500">Étages</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <DoorOpen className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {selectedCampus.buildings?.reduce(
                      (sum, b) => sum + (b.floors?.reduce((fSum, f) => fSum + (f.rooms?.length || 0), 0) || 0),
                      0
                    ) || 0}
                  </p>
                  <p className="text-sm text-gray-500">Salles</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <TreePine className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{selectedCampus.terrains?.length || 0}</p>
                  <p className="text-sm text-gray-500">Terrains</p>
                </div>
              </div>
            </div>
          </div>

          {/* Configuration Button */}
          {(!selectedCampus.buildings || selectedCampus.buildings.length === 0) && (
            <div className="mb-6 p-6 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-blue-900">Configurer l'infrastructure</h3>
                  <p className="text-blue-700 mt-1">
                    Ce campus n'a pas encore d'infrastructure configurée.{canManage && ' Lancez l\'assistant de configuration.'}
                  </p>
                </div>
                {canManage && (
                  <button
                    onClick={() => startWizard(selectedCampus)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Configurer
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Buildings Section */}
          <div className="bg-white rounded-lg border shadow-sm mb-6">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-600" />
                Bâtiments
              </h2>
              {canManage && (
                <button
                  onClick={() => {
                    setBuildingForm({ name: '' });
                    setAddingBuilding(true);
                  }}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter
                </button>
              )}
            </div>
            <div className="p-4">
              {selectedCampus.buildings && selectedCampus.buildings.length > 0 ? (
                <div className="space-y-3">
                  {selectedCampus.buildings.map((building) => (
                    <div key={building.id} className="border rounded-lg">
                      {/* Building Header */}
                      <div
                        className="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleBuilding(building.id)}
                      >
                        <div className="flex items-center gap-3">
                          {expandedBuildings.has(building.id) ? (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          )}
                          <Building2 className="w-5 h-5 text-blue-600" />
                          <span className="font-medium">
                            {building.name || 'Bâtiment principal'}
                          </span>
                          <span className="text-sm text-gray-500">
                            ({building.floors?.length || 0} étages)
                          </span>
                        </div>
                        {canManage && (
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => {
                                setBuildingForm({ name: building.name || '' });
                                setEditingBuilding(building);
                              }}
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() =>
                                setDeleteModal({ isOpen: true, type: 'building', item: building })
                              }
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Building Content (Floors) */}
                      {expandedBuildings.has(building.id) && (
                        <div className="border-t bg-gray-50 p-3">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-gray-700">Étages</span>
                            {canManage && (
                              <button
                                onClick={() => {
                                  setFloorForm({ floorNumber: (building.floors?.length || 0), name: '' });
                                  setAddingFloorTo(building.id);
                                }}
                                className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
                              >
                                <Plus className="w-3 h-3" />
                                Ajouter un étage
                              </button>
                            )}
                          </div>
                          {building.floors && building.floors.length > 0 ? (
                            <div className="space-y-2">
                              {building.floors.map((floor) => (
                                <div key={floor.id} className="bg-white border rounded-lg">
                                  {/* Floor Header */}
                                  <div
                                    className="p-2.5 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                                    onClick={() => toggleFloor(floor.id)}
                                  >
                                    <div className="flex items-center gap-2">
                                      {expandedFloors.has(floor.id) ? (
                                        <ChevronDown className="w-4 h-4 text-gray-400" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-400" />
                                      )}
                                      <Layers className="w-4 h-4 text-green-600" />
                                      <span className="text-sm font-medium">{floor.name}</span>
                                      <span className="text-xs text-gray-500">
                                        ({floor.rooms?.length || 0} salles)
                                      </span>
                                    </div>
                                    {canManage && (
                                      <div
                                        className="flex items-center gap-1"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <button
                                          onClick={() => {
                                            setFloorForm({ floorNumber: floor.floorNumber, name: floor.name });
                                            setEditingFloor({ floor, buildingId: building.id });
                                          }}
                                          className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                        >
                                          <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          onClick={() =>
                                            setDeleteModal({
                                              isOpen: true,
                                              type: 'floor',
                                              item: floor,
                                              parentIds: { buildingId: building.id },
                                            })
                                          }
                                          className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    )}
                                  </div>

                                  {/* Floor Content (Rooms) */}
                                  {expandedFloors.has(floor.id) && (
                                    <div className="border-t bg-gray-50 p-2.5">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-medium text-gray-600">Salles</span>
                                        {canManage && (
                                          <button
                                            onClick={() =>
                                              setAddingRoomTo({ floorId: floor.id, buildingId: building.id })
                                            }
                                            className="px-2 py-0.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-1"
                                          >
                                            <Plus className="w-3 h-3" />
                                            Ajouter
                                          </button>
                                        )}
                                      </div>
                                      {floor.rooms && floor.rooms.length > 0 ? (
                                        <div className="grid gap-2">
                                          {floor.rooms.map((room) => (
                                            <div
                                              key={room.id}
                                              className="bg-white border rounded p-2 flex items-center justify-between"
                                            >
                                              <div className="flex items-center gap-2">
                                                <DoorOpen className="w-4 h-4 text-purple-600" />
                                                <div>
                                                  <span className="text-sm font-medium">{room.name}</span>
                                                  <span className="text-xs text-gray-500 ml-2">
                                                    {getSubCategoryLabel(room.category, room.subCategory)}
                                                  </span>
                                                  {room.capacity && (
                                                    <span className="text-xs text-gray-400 ml-2">
                                                      ({room.capacity} places)
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              {canManage && (
                                                <div className="flex items-center gap-1">
                                                  <button
                                                    onClick={() => {
                                                      setRoomForm({
                                                        name: room.name,
                                                        category: room.category,
                                                        subCategory: room.subCategory,
                                                        capacity: room.capacity?.toString() || '',
                                                      });
                                                      setEditingRoom({
                                                        room,
                                                        floorId: floor.id,
                                                        buildingId: building.id,
                                                      });
                                                    }}
                                                    className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                                  >
                                                    <Pencil className="w-3 h-3" />
                                                  </button>
                                                  <button
                                                    onClick={() =>
                                                      setDeleteModal({
                                                        isOpen: true,
                                                        type: 'room',
                                                        item: room,
                                                        parentIds: { buildingId: building.id, floorId: floor.id },
                                                      })
                                                    }
                                                    className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                                                  >
                                                    <Trash2 className="w-3 h-3" />
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="text-xs text-gray-500 text-center py-2">
                                          Aucune salle configurée
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 text-center py-4">
                              Aucun étage configuré
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Aucun bâtiment configuré</p>
              )}
            </div>
          </div>

          {/* Terrains Section */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <TreePine className="w-5 h-5 text-amber-600" />
                Terrains extérieurs
              </h2>
              {canManage && (
                <button
                  onClick={() => {
                    setTerrainForm({ name: '', type: 'TERRAIN_FOOTBALL' });
                    setAddingTerrain(true);
                  }}
                  className="px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-1 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter
                </button>
              )}
            </div>
            <div className="p-4">
              {selectedCampus.terrains && selectedCampus.terrains.length > 0 ? (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {selectedCampus.terrains.map((terrain) => (
                    <div key={terrain.id} className="border rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <TreePine className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium">{terrain.name}</p>
                          <p className="text-sm text-gray-500">{getTerrainTypeLabel(terrain.type)}</p>
                        </div>
                      </div>
                      {canManage && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setTerrainForm({ name: terrain.name, type: terrain.type });
                              setEditingTerrain(terrain);
                            }}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              setDeleteModal({ isOpen: true, type: 'terrain', item: terrain })
                            }
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Aucun terrain configuré</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* ==================== MODALS ==================== */}

      {/* Configuration Wizard Modal */}
      {showWizard && wizardState && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowWizard(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {wizardState.step === 'buildings' && 'Configuration des bâtiments'}
                {wizardState.step === 'terrains' && 'Configuration des terrains'}
                {wizardState.step === 'floors' &&
                  `Configuration des étages - ${selectedCampus?.buildings[wizardState.currentBuildingIndex]?.name || 'Bâtiment'}`}
              </h3>

              {wizardState.step === 'buildings' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ce campus a-t-il plusieurs bâtiments ?
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={!wizardState.hasMultipleBuildings}
                          onChange={() =>
                            setWizardState({ ...wizardState, hasMultipleBuildings: false, buildings: [] })
                          }
                          className="w-4 h-4 text-blue-600"
                        />
                        Non
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={wizardState.hasMultipleBuildings}
                          onChange={() =>
                            setWizardState({ ...wizardState, hasMultipleBuildings: true })
                          }
                          className="w-4 h-4 text-blue-600"
                        />
                        Oui
                      </label>
                    </div>
                  </div>

                  {wizardState.hasMultipleBuildings && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nommez vos bâtiments
                      </label>
                      {wizardState.buildings.map((b, idx) => (
                        <div key={idx} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={b.name}
                            onChange={(e) => {
                              const newBuildings = [...wizardState.buildings];
                              newBuildings[idx].name = e.target.value;
                              setWizardState({ ...wizardState, buildings: newBuildings });
                            }}
                            placeholder={`Bâtiment ${idx + 1}`}
                            className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            onClick={() => {
                              const newBuildings = wizardState.buildings.filter((_, i) => i !== idx);
                              setWizardState({ ...wizardState, buildings: newBuildings });
                            }}
                            className="px-2 text-red-500 hover:text-red-700"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() =>
                          setWizardState({
                            ...wizardState,
                            buildings: [...wizardState.buildings, { name: '' }],
                          })
                        }
                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Ajouter un bâtiment
                      </button>
                    </div>
                  )}
                </div>
              )}

              {wizardState.step === 'terrains' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ce campus a-t-il des terrains extérieurs ?
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={!wizardState.hasTerrains}
                          onChange={() =>
                            setWizardState({ ...wizardState, hasTerrains: false, terrains: [] })
                          }
                          className="w-4 h-4 text-blue-600"
                        />
                        Non
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={wizardState.hasTerrains}
                          onChange={() => setWizardState({ ...wizardState, hasTerrains: true })}
                          className="w-4 h-4 text-blue-600"
                        />
                        Oui
                      </label>
                    </div>
                  </div>

                  {wizardState.hasTerrains && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Définissez vos terrains
                      </label>
                      {wizardState.terrains.map((t, idx) => (
                        <div key={idx} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={t.name}
                            onChange={(e) => {
                              const newTerrains = [...wizardState.terrains];
                              newTerrains[idx].name = e.target.value;
                              setWizardState({ ...wizardState, terrains: newTerrains });
                            }}
                            placeholder="Nom du terrain"
                            className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <select
                            value={t.type}
                            onChange={(e) => {
                              const newTerrains = [...wizardState.terrains];
                              newTerrains[idx].type = e.target.value;
                              setWizardState({ ...wizardState, terrains: newTerrains });
                            }}
                            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            {TERRAIN_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => {
                              const newTerrains = wizardState.terrains.filter((_, i) => i !== idx);
                              setWizardState({ ...wizardState, terrains: newTerrains });
                            }}
                            className="px-2 text-red-500 hover:text-red-700"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() =>
                          setWizardState({
                            ...wizardState,
                            terrains: [...wizardState.terrains, { name: '', type: 'TERRAIN_FOOTBALL' }],
                          })
                        }
                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Ajouter un terrain
                      </button>
                    </div>
                  )}
                </div>
              )}

              {wizardState.step === 'floors' && (
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center gap-2 mb-4">
                      <input
                        type="checkbox"
                        checked={wizardState.floorsConfig.hasBasement}
                        onChange={(e) =>
                          setWizardState({
                            ...wizardState,
                            floorsConfig: { ...wizardState.floorsConfig, hasBasement: e.target.checked },
                          })
                        }
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Ce bâtiment a un sous-sol
                      </span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 mb-4">
                      <input
                        type="checkbox"
                        checked={wizardState.floorsConfig.hasGroundFloor}
                        onChange={(e) =>
                          setWizardState({
                            ...wizardState,
                            floorsConfig: { ...wizardState.floorsConfig, hasGroundFloor: e.target.checked },
                          })
                        }
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Ce bâtiment a un rez-de-chaussée
                      </span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre d'étages (sans compter le sous-sol et le RDC)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={wizardState.floorsConfig.numberOfFloors}
                      onChange={(e) =>
                        setWizardState({
                          ...wizardState,
                          floorsConfig: {
                            ...wizardState.floorsConfig,
                            numberOfFloors: parseInt(e.target.value) || 0,
                          },
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowWizard(false);
                    setWizardState(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  onClick={handleWizardNext}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Continuer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Building Modal */}
      {(addingBuilding || editingBuilding) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => {
                setAddingBuilding(false);
                setEditingBuilding(null);
              }}
            />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingBuilding ? 'Modifier le bâtiment' : 'Ajouter un bâtiment'}
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du bâtiment (optionnel)
                </label>
                <input
                  type="text"
                  value={buildingForm.name}
                  onChange={(e) => setBuildingForm({ name: e.target.value })}
                  placeholder="Ex: Bâtiment A, Bloc Principal..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setAddingBuilding(false);
                    setEditingBuilding(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  onClick={editingBuilding ? handleUpdateBuilding : handleCreateBuilding}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingBuilding ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Floor Modal */}
      {(addingFloorTo || editingFloor) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => {
                setAddingFloorTo(null);
                setEditingFloor(null);
              }}
            />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingFloor ? 'Modifier l\'étage' : 'Ajouter un étage'}
              </h3>
              <div className="space-y-4">
                {!editingFloor && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Numéro de l'étage (0 = RDC)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={floorForm.floorNumber}
                      onChange={(e) =>
                        setFloorForm({
                          ...floorForm,
                          floorNumber: parseInt(e.target.value) || 0,
                          name: getFloorName(parseInt(e.target.value) || 0),
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de l'étage
                  </label>
                  <input
                    type="text"
                    value={floorForm.name}
                    onChange={(e) => setFloorForm({ ...floorForm, name: e.target.value })}
                    placeholder="Ex: Rez-de-chaussée, 1er étage..."
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setAddingFloorTo(null);
                    setEditingFloor(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  onClick={editingFloor ? handleUpdateFloor : handleCreateFloor}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingFloor ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Room Modal */}
      {(addingRoomTo || editingRoom) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => {
                setAddingRoomTo(null);
                setEditingRoom(null);
              }}
            />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingRoom ? 'Modifier la salle' : 'Ajouter une salle'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la salle</label>
                  <input
                    type="text"
                    value={roomForm.name}
                    onChange={(e) => setRoomForm({ ...roomForm, name: e.target.value })}
                    placeholder="Ex: Salle 101, Salle Multimédia..."
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                  <select
                    value={roomForm.category}
                    onChange={(e) => {
                      const category = e.target.value as RoomCategory;
                      const firstSubCat = ROOM_SUB_CATEGORIES[category]?.[0]?.value || '';
                      setRoomForm({ ...roomForm, category, subCategory: firstSubCat });
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.entries(ROOM_CATEGORY_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de salle</label>
                  <select
                    value={roomForm.subCategory}
                    onChange={(e) => setRoomForm({ ...roomForm, subCategory: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {ROOM_SUB_CATEGORIES[roomForm.category]?.map((subCat) => (
                      <option key={subCat.value} value={subCat.value}>
                        {subCat.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacité (optionnel)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={roomForm.capacity}
                    onChange={(e) => setRoomForm({ ...roomForm, capacity: e.target.value })}
                    placeholder="Nombre de places"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setAddingRoomTo(null);
                    setEditingRoom(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  onClick={editingRoom ? handleUpdateRoom : handleCreateRoom}
                  disabled={saving || !roomForm.name}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingRoom ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Terrain Modal */}
      {(addingTerrain || editingTerrain) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => {
                setAddingTerrain(false);
                setEditingTerrain(null);
              }}
            />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingTerrain ? 'Modifier le terrain' : 'Ajouter un terrain'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom du terrain</label>
                  <input
                    type="text"
                    value={terrainForm.name}
                    onChange={(e) => setTerrainForm({ ...terrainForm, name: e.target.value })}
                    placeholder="Ex: Terrain de foot principal..."
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de terrain</label>
                  <select
                    value={terrainForm.type}
                    onChange={(e) => setTerrainForm({ ...terrainForm, type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {TERRAIN_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setAddingTerrain(false);
                    setEditingTerrain(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  onClick={editingTerrain ? handleUpdateTerrain : handleCreateTerrain}
                  disabled={saving || !terrainForm.name}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingTerrain ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        title={`Supprimer ${
          deleteModal.type === 'building'
            ? 'le bâtiment'
            : deleteModal.type === 'floor'
            ? "l'étage"
            : deleteModal.type === 'room'
            ? 'la salle'
            : 'le terrain'
        }`}
        message={
          <span>
            Êtes-vous sûr de vouloir supprimer{' '}
            <strong>{deleteModal.item?.name || 'cet élément'}</strong> ?
            {deleteModal.type === 'building' && ' Tous les étages et salles associés seront également supprimés.'}
            {deleteModal.type === 'floor' && ' Toutes les salles de cet étage seront également supprimées.'}
          </span>
        }
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={() => {
          if (deleteModal.type === 'building') handleDeleteBuilding();
          else if (deleteModal.type === 'floor') handleDeleteFloor();
          else if (deleteModal.type === 'room') handleDeleteRoom();
          else if (deleteModal.type === 'terrain') handleDeleteTerrain();
        }}
        onCancel={() => setDeleteModal({ isOpen: false, type: 'building', item: null })}
      />
    </div>
  );
};

export default GestionCampusPage;
